import { FileText, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { mxn } from "@/lib/format";
import { productoCredito } from "@/lib/creditos";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const esContrato = (d) =>
  d.tipo_canonico === "contrato" || (d.tipo_documento || "") === "contrato_firmado";

// El contrato de un crédito se toma AUTOMÁTICAMENTE del que se subió en su solicitud:
// por `credito_id` o, cuando el documento nació antes del crédito, por el `referencia_id`
// de la solicitud que lo originó (convención por trámite de `doc_canonico`). NUNCA se cruza
// con el contrato de otro crédito.
export const contratoDeCredito = (documentos, credito) =>
  (documentos || []).find((d) =>
    esContrato(d) && d.estatus === "Vigente"
    && (d.credito_id === credito.id
        || (!d.credito_id && credito.solicitud_id && d.referencia_id === credito.solicitud_id)))
  || null;

const folioDe = (cr) => cr.numero_credito || cr.id?.slice(0, 8) || "—";
const idDe = (cr) => String(folioDe(cr)).replace(/[^A-Za-z0-9-]/g, "");

/** Créditos del cliente con su contrato firmado. Solo consulta: el contrato se carga en la
 *  solicitud del crédito y aquí únicamente se ve. */
export function CreditosContratos({ creditos = [], documentos = [] }) {
  const abrir = async (docId) => {
    try {
      const { data } = await api.post(`/documentos/${docId}/signed-url`);
      const url = data.url.startsWith("http") ? data.url : `${BACKEND_URL}${data.url}`;
      window.open(url, "_blank", "noopener");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!creditos.length) return null;

  return (
    <section className="mb-4 rounded-lg border border-slate-200 bg-white p-3" data-testid="c360-creditos-contratos">
      <div className="mb-2 flex items-center gap-2">
        <FileText className="h-4 w-4 text-primary" />
        <h4 className="text-xs font-semibold uppercase tracking-wide text-primary">Créditos del cliente</h4>
        <Badge variant="secondary" data-testid="c360-cc-total">{creditos.length}</Badge>
      </div>
      <p className="mb-3 text-[11px] text-slate-500">
        Cada crédito con su contrato firmado, tomado de su solicitud. Consulta únicamente.
      </p>
      <div className="space-y-2">
        {creditos.map((cr) => {
          const contrato = contratoDeCredito(documentos, cr);
          const t = idDe(cr);
          return (
            <div key={cr.id} className="rounded-md border border-slate-200 bg-slate-50 p-2.5"
                 data-testid={`c360-cc-row-${t}`}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="font-mono text-sm font-semibold text-slate-900" data-testid={`c360-cc-folio-${t}`}>
                  {folioDe(cr)}
                </span>
                <span className="text-xs text-slate-600" data-testid={`c360-cc-producto-${t}`}>
                  {productoCredito(cr)}
                </span>
                <span className="text-xs font-semibold text-slate-800" data-testid={`c360-cc-monto-${t}`}>
                  {mxn(cr.monto_original || cr.monto || 0)}
                </span>
                <Badge variant="outline" data-testid={`c360-cc-estatus-${t}`}>{cr.estatus || "—"}</Badge>
              </div>
              {contrato && (
                <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-slate-200 pt-2">
                  <Badge className="border-emerald-200 bg-emerald-100 text-emerald-700"
                         data-testid={`c360-cc-contrato-${t}`}>
                    Contrato firmado · Vigente
                  </Badge>
                  <span className="max-w-[16rem] truncate text-[11px] text-slate-500" title={contrato.nombre_archivo}>
                    {contrato.nombre_archivo} · {contrato.fecha_carga}
                  </span>
                  <Button variant="outline" size="sm" onClick={() => abrir(contrato.id)}
                          data-testid={`c360-cc-ver-${t}`}>
                    <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Ver
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default CreditosContratos;
